import React, { memo, useCallback, useEffect, useRef } from 'react';
import classNames from 'classnames';
import Slider from 'react-slick';
import { useEnvironment } from '@wix/yoshi-flow-editor';
import { resolveId } from '@wix/communities-blog-client-common';
import { type NormalizedPost } from '@app/external/common/types';
import { PostPageRelatedPostsSectionItem } from './post-page-related-posts-section-item';
import styles from './post-page-related-posts-section.scss';

type Props = {
  postsToShow: NormalizedPost[];
};

const applyInertToSlides = (container: HTMLElement | null) => {
  if (!container) {
    return;
  }
  const slides = container.querySelectorAll('.slick-slide');

  slides.forEach((slide) => {
    if (slide.classList.contains('slick-active')) {
      slide.removeAttribute('inert');
    } else {
      slide.setAttribute('inert', 'true');
    }
  });
};

const removeInertFromSlides = (container: HTMLElement | null) => {
  if (!container) {
    return;
  }
  container.querySelectorAll('.slick-slide[inert]').forEach((slide) => {
    slide.removeAttribute('inert');
  });
  // Force WebKit to invalidate its hit-test cache after removing inert
  void container.offsetHeight;
};

const arePostListsEqual = (a: NormalizedPost[], b: NormalizedPost[]) => {
  if (a === b) {
    return true;
  }
  if (a.length !== b.length) {
    return false;
  }
  for (let i = 0; i < a.length; i += 1) {
    if (resolveId(a[i]) !== resolveId(b[i])) {
      return false;
    }
  }
  return true;
};

// `useEnvironment` subscribes this component to the WidgetEnvironment context,
// which the yoshi WidgetWrapper re-emits with a fresh object on every host
// render. On iOS Safari the address-bar show/hide during scroll continuously
// mutates `host.dimensions`, re-emitting that context and re-rendering every
// consumer. Isolating the consumption to a tiny wrapper keeps the outer
// slider memoised (no context subscription) so react-slick never receives a
// fresh `children` array, its `componentDidUpdate -> updateState` path stays
// idle, and `.slick-slide` DOM nodes are not churned mid-tap.
const Slide = ({ post }: { post: NormalizedPost }) => {
  const { isRTL } = useEnvironment();
  return (
    <div className={styles.slide} dir={isRTL ? 'rtl' : undefined}>
      <PostPageRelatedPostsSectionItem post={post} />
    </div>
  );
};

export const PostPageRelatedPostsMobileSlider = memo(
  ({ postsToShow }: Props) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

    useEffect(() => {
      const timeout = setTimeout(() => applyInertToSlides(containerRef.current), 1000);
      return () => {
        clearTimeout(timeout);
        clearTimeout(timeoutRef.current);
      };
    }, []);

    const handleBeforeChange = useCallback(() => {
      clearTimeout(timeoutRef.current);
      removeInertFromSlides(containerRef.current);
    }, []);

    const handleAfterChange = useCallback(() => {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => applyInertToSlides(containerRef.current), 50);
    }, []);

    return (
      <div dir="ltr" ref={containerRef}>
        <Slider
          arrows={false}
          className={styles.slider}
          dots={true}
          useCSS={true}
          infinite={true}
          customPaging={Dot}
          centerMode={true}
          centerPadding="30px"
          slidesToShow={1}
          slidesToScroll={1}
          beforeChange={handleBeforeChange}
          afterChange={handleAfterChange}
        >
          {postsToShow.map((relatedPost) => (
            <Slide key={resolveId(relatedPost)} post={relatedPost} />
          ))}
        </Slider>
      </div>
    );
  },
  (prev, next) => arePostListsEqual(prev.postsToShow, next.postsToShow),
);

PostPageRelatedPostsMobileSlider.displayName = 'PostPageRelatedPostsMobileSlider';

const Dot = () => (
  <div className={styles.dotContainer}>
    <div className={classNames(styles.dot, 'blog-text-background-color')} />
    <div className={classNames(styles.dot, styles.dotActive, 'blog-button-background-color')} />
  </div>
);
